import { useCallback, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { listCardsPaged } from "../api/cards";
import { createSalesOrder } from "../api/salesOrders";
import ReferencePickerDialog from "./ReferencePickerDialog";

export default function SalesOrderModal({ onClose, onSaved }) {
  const [orderNo, setOrderNo] = useState("");
  const [injetNo, setInjetNo] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [customerPickerOpen, setCustomerPickerOpen] = useState(false);
  const loadCustomers = useCallback(({ page, search }) => listCardsPaged({ type: "customer", search, page, limit: 10 }), []);

  async function save() {
    setError("");
    const o = String(orderNo || "").trim();
    const injet = String(injetNo || "").trim();
    if (!o) {
      setError("Sipariş no gerekli.");
      return;
    }

    if (!injet) {
      setError("Injet No gerekli.");
      return;
    }

    setSaving(true);
    try {
      await createSalesOrder({
        order_no: o,
        injet_no: injet,
        customer_card_id: customerId ? Number(customerId) : null,
        note: String(note || "").trim() ? String(note || "").trim() : null,
      });
      await onSaved?.();
      onClose?.();
    } catch (err) {
      console.error("SALES ORDER SAVE ERROR", err);
      const status = err?.response?.status;
      const dataObj = err?.response?.data;
      const message =
        dataObj?.error?.message ||
        dataObj?.message ||
        (typeof dataObj === "string" ? dataObj : null) ||
        err?.message ||
        null;
      setError(`Kaydedilemedi${status ? ` (HTTP ${status})` : ""}${message ? `: ${message}` : ""}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
    <Dialog open onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4, overflow: "hidden", boxShadow: "0 24px 70px rgba(15, 23, 42, 0.22)" } }}>
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{ px: { xs: 2.5, sm: 3.5 }, py: 2.5, color: "#fff", background: "linear-gradient(135deg, #312e81 0%, #4f46e5 56%, #6366f1 100%)" }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{ width: 42, height: 42, borderRadius: 2.25, display: "grid", placeItems: "center", bgcolor: "rgba(255,255,255,0.16)", border: "1px solid rgba(255,255,255,0.24)", fontSize: 19 }}>+</Box>
            <Box>
              <Typography variant="h6" fontWeight={800}>Yeni Satış Siparişi</Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>Sipariş bilgilerini girin, müşteriyle ilişkilendirin ve kaydı oluşturun.</Typography>
            </Box>
          </Stack>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 2, sm: 3 }, bgcolor: "#f7f8fc" }}>
        {error ? <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert> : null}
        <Paper elevation={0} sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3, border: "1px solid #e7eaf3", bgcolor: "#fff", mb: 2 }}>
          <Box sx={{ mb: 2 }}>
            <Typography fontWeight={800}>Sipariş bilgileri</Typography>
            <Typography variant="body2" color="text.secondary">Sipariş numarası ve Injet numarası tüm kalemler için ortak kullanılır.</Typography>
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
            <TextField sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.25 } }} label="Sipariş No" placeholder="Örn. CFN26000752" value={orderNo} onChange={(e) => setOrderNo(e.target.value)} required disabled={saving} fullWidth autoFocus />
            <TextField sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.25 } }} label="Injet No" placeholder="Örn. INJ-2026-001" value={injetNo} onChange={(e) => setInjetNo(e.target.value)} required disabled={saving} fullWidth />
          </Box>
        </Paper>

        <Paper elevation={0} sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3, border: "1px solid #e7eaf3", bgcolor: "#fff", mb: 2 }}>
          <Box sx={{ mb: 1.5 }}>
            <Stack direction="row" spacing={1} alignItems="center"><Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#6366f1" }} /><Typography fontWeight={800}>Müşteri bağlantısı</Typography><Typography variant="caption" color="text.secondary">İsteğe bağlı</Typography></Stack>
            <Typography variant="body2" color="text.secondary">Siparişi müşteri kartına bağlayarak takipte doğru müşteri bilgisinin görünmesini sağlayın.</Typography>
          </Box>
          <Box sx={{ border: "1px solid", borderColor: customerId ? "#c7d2fe" : "#dbe1ec", borderRadius: 2.25, px: 1.5, py: 1.1, bgcolor: customerId ? "#f5f7ff" : "#fff" }}>
            <Typography variant="caption" color="text.secondary" display="block">Müşteri</Typography>
            <Button
              variant="text"
              size="medium"
              disabled={saving}
              onClick={() => setCustomerPickerOpen(true)}
              sx={{
                justifyContent: "flex-start",
                textAlign: "left",
                textTransform: "none",
                minWidth: 0,
                px: 0,
                color: customerId ? "#3730a3" : "text.secondary",
                fontWeight: customerId ? 700 : 400,
                "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
              }}
            >
              {customerId ? customerName : "Seçilmedi"}
            </Button>
            {customerId ? <Button size="small" color="inherit" onClick={() => { setCustomerId(""); setCustomerName(""); }}>Temizle</Button> : null}
          </Box>

        </Paper>
        <Paper elevation={0} sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3, border: "1px solid #e7eaf3", bgcolor: "#fff" }}>
          <Typography fontWeight={800} sx={{ mb: 0.5 }}>Not</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Siparişle ilgili açıklama veya takip notu ekleyebilirsiniz.</Typography>
          <TextField sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.25 } }} label="Not (isteğe bağlı)" value={note} onChange={(e) => setNote(e.target.value)} disabled={saving} fullWidth multiline minRows={2} />
        </Paper>
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2, sm: 3.5 }, py: 2, bgcolor: "#fff", borderTop: "1px solid #e7eaf3", justifyContent: "space-between" }}>
        <Typography variant="caption" color="text.secondary">* Sipariş No ve Injet No zorunludur.</Typography>
        <Stack direction="row" spacing={1}><Button onClick={onClose} color="inherit" disabled={saving}>İptal</Button><Button onClick={save} variant="contained" disabled={saving} sx={{ px: 2.5, borderRadius: 2, boxShadow: "0 8px 18px rgba(79, 70, 229, 0.24)" }}>{saving ? "Kaydediliyor..." : "Siparişi Kaydet"}</Button></Stack>
      </DialogActions>
    </Dialog>
    <ReferencePickerDialog
      open={customerPickerOpen}
      title="Müşteri Seç"
      searchPlaceholder="Müşteri adı ile ara..."
      fetchPage={loadCustomers}
      getPrimary={(item) => item.name}
      getSecondary={(item) => item.code || "Müşteri kartı"}
      onClose={() => setCustomerPickerOpen(false)}
      onSelect={(item) => { setCustomerId(String(item.id)); setCustomerName(item.name); setCustomerPickerOpen(false); }}
    />
    </>
  );
}
